import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, ChevronRight, Users, Music, Heart, MapPin, Calendar, Bell, Award } from 'lucide-react';
import Navigation from '../components/Navigation';
import { mockData } from '../mock';

const ExperiencePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const atmosphereSlides = [
    { image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1600&h=900&fit=crop', caption: 'Energy' },
    { image: 'https://images.unsplash.com/photo-1470229538611-16ba8c7ffbd7?w=1600&h=900&fit=crop', caption: 'Worship' },
    { image: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=1600&h=900&fit=crop', caption: 'Growth' },
    { image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1600&h=900&fit=crop', caption: 'Impact' }
  ];

  const testimonials = [
    {
      quote: "I came seeking clarity and left with a calling. The connections I made here changed my life.",
      author: "Sarah M.",
      role: "Entrepreneur",
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop'
    },
    {
      quote: "KNOWN CON isn't just a conference—it's a family. The worship, teaching, and community are unmatched.",
      author: "Michael T.",
      role: "Pastor",
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop'
    },
    {
      quote: "Every session equipped me with practical tools. I left inspired and ready to impact my community.",
      author: "Jessica L.",
      role: "Youth Leader",
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop'
    }
  ];

  const experienceHighlights = [
    {
      title: 'Leadership Labs',
      description: 'Interactive workshops with industry leaders sharing real-world strategies',
      image: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=600&h=400&fit=crop'
    },
    {
      title: 'Faith & Culture Conversations',
      description: 'Panel discussions bridging faith, art, media, and cultural influence',
      image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=600&h=400&fit=crop'
    },
    {
      title: 'Business & Purpose Summit',
      description: 'Kingdom entrepreneurship and marketplace ministry insights',
      image: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&h=400&fit=crop'
    },
    {
      title: 'Marketplace & Vendors',
      description: 'Explore faith-based products, books, resources, and ministry tools',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop'
    },
    {
      title: 'Treasure Hunt Adventure',
      description: 'Prophetic activation exercise connecting attendees through divine encounters',
      image: 'https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?w=600&h=400&fit=crop'
    },
    {
      title: 'Evening Celebration',
      description: 'Powerful worship nights with renowned artists and spontaneous praise',
      image: 'https://images.unsplash.com/photo-1470229538611-16ba8c7ffbd7?w=600&h=400&fit=crop'
    }
  ];

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % atmosphereSlides.length);
    }, 5000);

    return () => clearInterval(slideInterval);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Header */}
      <div className="relative h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          {/* Breadcrumb */}
          <div className="flex items-center justify-center gap-2 text-sm mb-6 text-gray-300">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <span className="text-white font-medium">The KNOWN CON Experience</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            The KNOWN CON <span className="text-[#CFA64B]">Experience</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200 leading-relaxed">
            More than a conference — it's a movement of faith, culture, and purpose.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold text-lg px-10 py-6 shadow-lg hover:shadow-xl transition-all">
              View Schedule
            </Button>
            <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/40 hover:border-white text-lg px-10 py-6 backdrop-blur-sm">
              Buy Tickets
            </Button>
          </div>
        </div>
      </div>

      {/* What to Expect */}
      <div className="py-24 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">What to Expect</h2>
            <p className="text-xl text-gray-600">Three days of transformation await</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#0B1B3B] to-[#1e3a8a] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#0B1B3B] group-hover:text-[#CFA64B] transition-colors">Inspiring Sessions</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Transformative keynotes and workshops from world-class speakers who will challenge and equip you.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#CFA64B] to-[#d97706] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Music className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#0B1B3B] group-hover:text-[#CFA64B] transition-colors">Powerful Worship</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Music and faith moments that move you—experience worship that creates lasting encounters with God.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Heart className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-[#0B1B3B] group-hover:text-[#CFA64B] transition-colors">Unforgettable Connections</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Community and mentorship that last beyond the weekend—relationships that change everything.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Experience Highlights Gallery */}
      <div className="py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Experience Highlights</h2>
            <p className="text-xl text-gray-600">Discover what makes KNOWN CON unforgettable</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {experienceHighlights.map((highlight, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img
                  src={highlight.image}
                  alt={highlight.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent group-hover:from-[#0B1B3B]/95 transition-all duration-500" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#CFA64B] transition-colors">
                    {highlight.title}
                  </h3>
                  <p className="text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-500 leading-relaxed">
                    {highlight.description}
                  </p>
                </div>

                {/* Gold border glow on hover */}
                <div className="absolute inset-0 border-4 border-[#CFA64B] opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Atmosphere Slider */}
      <div className="relative h-[500px] overflow-hidden">
        {/* Slides */}
        {atmosphereSlides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              idx === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${slide.image}')` }}
            />
            <div className="absolute inset-0 bg-black/60" />
            
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <h2 className="text-6xl md:text-8xl font-bold mb-8 text-[#CFA64B] animate-fade-in">
                {slide.caption}
              </h2>
              <p className="text-2xl md:text-3xl font-light tracking-wider">
                See. Feel. Believe.
              </p>
            </div>
          </div>
        ))}

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {atmosphereSlides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === currentSlide ? 'w-12 bg-[#CFA64B]' : 'w-2 bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-24 bg-gradient-to-br from-[#0B1B3B]/5 via-white to-[#CFA64B]/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Stories from the Known Community</h2>
            <p className="text-xl text-gray-600">Real people, real transformation</p>
          </div>

          <div className="relative">
            <Card className="border-none shadow-2xl">
              <CardContent className="p-12">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <img
                    src={testimonials[currentTestimonial].image}
                    alt={testimonials[currentTestimonial].author}
                    className="w-32 h-32 rounded-full object-cover shadow-xl ring-4 ring-[#CFA64B]/20"
                  />
                  <div className="flex-1 text-center md:text-left">
                    <div className="text-6xl text-[#CFA64B] mb-4">"</div>
                    <p className="text-2xl text-gray-700 leading-relaxed mb-6 italic">
                      {testimonials[currentTestimonial].quote}
                    </p>
                    <div>
                      <p className="text-xl font-bold text-[#0B1B3B]">
                        — {testimonials[currentTestimonial].author}
                      </p>
                      <p className="text-gray-600">
                        {testimonials[currentTestimonial].role}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 rounded-full bg-white border-2 border-[#CFA64B] hover:bg-[#CFA64B] hover:text-white flex items-center justify-center transition-all hover:scale-110 shadow-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextTestimonial}
                className="w-12 h-12 rounded-full bg-white border-2 border-[#CFA64B] hover:bg-[#CFA64B] hover:text-white flex items-center justify-center transition-all hover:scale-110 shadow-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentTestimonial(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    idx === currentTestimonial ? 'w-8 bg-[#CFA64B]' : 'w-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sponsors Strip */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-6">
          <h3 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-12">
            Powered by Our Partners
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-12">
            {mockData.partners.map((partner) => (
              <div
                key={partner.id}
                className="grayscale hover:grayscale-0 opacity-50 hover:opacity-100 transition-all duration-300 hover:scale-110"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-12 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Prepare for Your Experience */}
      <div className="py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Prepare for Your Experience</h2>
            <p className="text-xl text-gray-600">Everything you need to know before you arrive</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center group-hover:bg-[#CFA64B] transition-all">
                  <MapPin className="w-8 h-8 text-[#CFA64B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-[#0B1B3B] mb-2">Plan Your Trip</h3>
                <p className="text-sm text-gray-600 mb-4">Hotel & travel info</p>
                <a href="#travel" className="text-[#CFA64B] hover:underline font-semibold text-sm">Learn More →</a>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center group-hover:bg-[#CFA64B] transition-all">
                  <Bell className="w-8 h-8 text-[#CFA64B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-[#0B1B3B] mb-2">Stay Connected</h3>
                <p className="text-sm text-gray-600 mb-4">Newsletter updates</p>
                <a href="#newsletter" className="text-[#CFA64B] hover:underline font-semibold text-sm">Sign Up →</a>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center group-hover:bg-[#CFA64B] transition-all">
                  <Award className="w-8 h-8 text-[#CFA64B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-[#0B1B3B] mb-2">Activate Your Badge</h3>
                <p className="text-sm text-gray-600 mb-4">Quick registration</p>
                <a href="#badge" className="text-[#CFA64B] hover:underline font-semibold text-sm">Activate →</a>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300 group cursor-pointer">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center group-hover:bg-[#CFA64B] transition-all">
                  <Calendar className="w-8 h-8 text-[#CFA64B] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-[#0B1B3B] mb-2">Know Before You Go</h3>
                <p className="text-sm text-gray-600 mb-4">Attendee guide</p>
                <a href="#guide" className="text-[#CFA64B] hover:underline font-semibold text-sm">View Guide →</a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Join the Movement CTA */}
      <div className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a]/95 to-[#CFA64B]/90" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            This isn't just an event — it's where faith and purpose ignite.
          </h2>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Join thousands of believers, leaders, and change-makers this August
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold text-lg px-12 py-6 shadow-xl hover:shadow-2xl transition-all hover:scale-105">
              Buy Tickets
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent hover:bg-white/10 text-white border-white border-2 hover:border-white text-lg px-12 py-6">
              View Schedule
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-[#CFA64B]">KNOWN CON</h3>
              <p className="text-gray-400">Where belief meets impact.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a></li>
                <li><a href="/speakers" className="hover:text-[#CFA64B] transition-colors">Speakers</a></li>
                <li><a href="/experience" className="hover:text-[#CFA64B] transition-colors">Experience</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Get Involved</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#sponsor" className="hover:text-[#CFA64B] transition-colors">Become a Sponsor</a></li>
                <li><a href="#volunteer" className="hover:text-[#CFA64B] transition-colors">Volunteer</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Contact</h4>
              <p className="text-gray-400 text-sm">info@knowncon.com</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            © 2025 KNOWN CON. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ExperiencePage;